import { Banknote  } from '@lucide/vue'

import { AssetWizardFlow } from '@/store/ai/assetWizard/utils/types'
import { TagRef } from '@/entities/tags/utils/types'

import {
  amountField,
  assetNameField,
  currencyField,
  dateField,
  descriptionField,
} from '../utils/fields'
import { OWNERSHIP_STEP, SUMMARY_STEP, TAGS_STEP } from '../utils/steps'
import { WizardDefinition } from '../utils/types'

import { loanSchema } from './schema'
import { create } from './create'

/**
 * The type every record this wizard creates carries. Asking for it would be
 * asking the user to repeat the wizard they just chose — and the answer is not
 * theirs to vary: a mortgage taken against a purchase belongs to the
 * real-estate-with-financing flow, a line of credit to its own.
 *
 * A literal because the app has no asset-type vocabulary to read from — types
 * come from the repository's transaction settings at runtime. One constant, so
 * wiring the real list later is one edit. Not a field: choosing this wizard
 * has already said the type, and showing it would be asking the user to
 * confirm what they cannot change.
 */
const LIABILITY_TYPE = 'loan'

/**
 * Applied the moment the wizard opens — including when the assistant lands on
 * the summary. Same pairs the legacy liability wizard pre-filled. No ids:
 * whether the repository has them is resolved against the catalogue later, and
 * a pair it lacks still shows as a chip and is created on submit.
 */
const DEFAULT_TAGS: TagRef[] = [
  { tag_name: 'Asset Class', tag_value: 'Borrowings' },
  { tag_name: 'Investment Strategy', tag_value: 'Mortgage' },
]

/**
 * Money borrowed with no asset bought in the same breath. A mortgage taken to
 * buy a property belongs in the real-estate-with-financing wizard, which links
 * the two records; this one stands alone.
 */
export const loanWizard: WizardDefinition = {
  flow: AssetWizardFlow.LOAN,
  label: 'Loan',
  icon: Banknote,
  schema: loanSchema,
  steps: [
    OWNERSHIP_STEP,
    {
      key: 'liability',
      label: 'Liability',
      description:
        'A liability is a debt or obligation that a company or individual owes to another party. It is a financial liability that must be repaid in the future.',
      fields: [
        // Full width: a liability's name is a phrase — "Bank credit line,
        // Northern Trust" — not a figure to fit beside another.
        assetNameField('Name', 'Ex. Bank credit line'),
        currencyField(),
        dateField('Date'),
        amountField('Amount'),
        { ...descriptionField(), span: 2 },
      ],
    },
    TAGS_STEP,
    SUMMARY_STEP,
  ],
  records: [
    {
      key: 'loan',
      label: 'Loan',
      icon: Banknote,
      tone: 'brand',
      transactionType: 'add liability', 
      heading: 'assetName',
      fields: ['amount', 'date', 'currency', 'description'],
    },
  ],
  constants: { assetType: LIABILITY_TYPE },
  defaultTags: DEFAULT_TAGS,
  create,
}
